import React, {Component} from 'react'
import PropTypes from 'prop-types'
import {withTranslation} from 'react-i18next';
import {Field, FieldArray, reduxForm} from 'redux-form'
import {connect} from 'react-redux';
import {arrayMove} from 'react-sortable-hoc';
import {withRouter} from 'react-router-dom';
import {textField} from '../Form/textField'
import {textArea} from '../Form/textArea'
import {checkbox} from '../Form/checkbox'
import {childrenBirthYearsArray} from '../Form/childrenBirthYearsArray'
import {booleanRadio} from '../Form/booleanRadio'
import PartialApplicant from './PartialApplicant'
import BasketSummaryNew from './BasketSummaryNew'
import BasketSummaryEdit from './BasketSummaryEdit'
import {flattenJsonErrors} from '../Form/flattenJsonErrors'
import validate from '../../validators/applicationValidator'
import * as applicationAction from '../../actions/applicationAction';
import * as callForApplicationAction from '../../actions/callForApplicationAction';
import {flash} from "../FlashMessage";

// CSS
import styles from './ApplicationForm.css'

// Decorate the class.
@withRouter
@withTranslation()
@connect(
	state => ({ // mapStateToProps
		currentCallForApplication: state.callForApplication.current,
		applicationForm: state.form.applicationForm,
		application: state.application.one,
		initialValues: state.application.one
	}),
	{
		initializeApplication: applicationAction.initializeOne,
		loadApplication: applicationAction.loadOne,
		create: applicationAction.create,
		update: applicationAction.update,
		fetchCurrentCallForApplication: callForApplicationAction.fetchCurrent
	}
)
@reduxForm({
	form: 'applicationForm',
	validate,
	onSubmitFail: errors => {
		// We flatten the array of errors to retrieve the id
		// E.g. foo: {bar: 'baz'} => foo.bar: baz
		const flattenErrors = flattenJsonErrors(errors)

		// Second round, we optimize the flatten errors and keep only the string.
		for (let [key, value] of Object.entries(flattenErrors)) {
			if (typeof value !== 'string') {
				delete flattenErrors[key]
			}
		}

		if (Object.keys(flattenErrors).length > 0) {
		  // set url hash to topmost error
		  const id = Object.keys(flattenErrors)[0]
		  window.location.hash = id
		  if (document.getElementById(id)) {
		    document.getElementById(id).focus();
		    document.getElementById(id).classList.add('error');
		  }
		}
	}
})

export default class ApplicationForm extends Component {

	/**
	 * @type {{params: (string|d.object|string), flash: (*|null)}}
	 */
	static contextTypes = {
		params: PropTypes.object,
	}

	constructor(props) {
		super(props)

		this.state = {
			callForApplicationItems: [],
			unavailableCallForApplicationItems: [],
			isLoaded: false,
			submitting: false
		}

		this.movePositionInBasket = this.movePositionInBasket.bind(this)
		this.removeFromBasket = this.removeFromBasket.bind(this)
		this.save = this.save.bind(this)
		this.renderAdditionalApplicants = this.renderAdditionalApplicants.bind(this)
		this.retrieveItem = this.retrieveItem.bind(this)
		this.renderForm = this.renderForm.bind(this)
	}

	/**
	 * @return {object|null}
	 */
	retrieveItem(callForApplicationItem) {
		let foundItem = null
		this.props.currentCallForApplication.callForApplicationItems.map(_callForApplicationItem => {

			if (_callForApplicationItem.__identity === callForApplicationItem.__identity) {
				foundItem = _callForApplicationItem;
			}
		})

		return foundItem;
	}

	/**
	 * @return {boolean}
	 */
	isEditMode() {
		return typeof AblCh.application === 'object'
	}

	/**
	 * @return void
	 */
	componentDidMount() {

		const specialCallForApplicationIdentifier = AblCh.callForApplicationIdentifier;

		// Trick! Whenever we automatically scroll up or down, we want to go a little bit further because of sticky header
		window.addEventListener("hashchange", function() {
			window.scrollTo(window.scrollX, window.scrollY - 150)
		})

		if (this.isEditMode()) {
			this.props.initializeApplication()
			this.setState({isLoaded: true})
		} else {
			this.props.fetchCurrentCallForApplication(specialCallForApplicationIdentifier)
				.then(callForApplication => {
					if (callForApplication) {
						const key = this.getStorageKey()
						const callForApplicationItemsFromStorage = localStorage.getItem(key)
							? JSON.parse(localStorage.getItem(key))
							: []

						const callForApplicationItems = []
						callForApplicationItemsFromStorage.map(callForApplicationItem => {
							const retrievedCallForApplicationItem = this.retrieveItem(callForApplicationItem)
							if (retrievedCallForApplicationItem) {
								callForApplicationItems.push(retrievedCallForApplicationItem)
							}
						})

						this.saveStateInLocalStorage(callForApplicationItems)
						this.setState({callForApplicationItems: callForApplicationItems})

						this.props.initializeApplication()
					}
					this.setState({isLoaded: true})
				})
		}
	}

	/**
	 * @param {object} callForApplicationItems
	 */
	saveStateInLocalStorage(callForApplicationItems) {
		localStorage.setItem(this.getStorageKey(), JSON.stringify(callForApplicationItems)) // persist in localStorage
	}

	/**
	 * @param {int} oldIndex
	 * @param {int} newIndex
	 */
	movePositionInBasket(oldIndex, newIndex) {
		const callForApplicationItems = this.state.callForApplicationItems
		const callForApplicationItemsWithNewOrder = arrayMove(callForApplicationItems, oldIndex, newIndex)
		this.setState({callForApplicationItems: callForApplicationItemsWithNewOrder})
		this.saveStateInLocalStorage(callForApplicationItemsWithNewOrder)
	}

	/**
	 * @param callForApplicationItem
	 */
	removeFromBasket(callForApplicationItem) {
		const position = this.getPositionInBasket(callForApplicationItem)
		const callForApplicationItems = this.state.callForApplicationItems
		callForApplicationItems.splice(position, 1)
		this.setState({callForApplicationItems: callForApplicationItems})
		this.saveStateInLocalStorage(callForApplicationItems)
	}

	/**
	 * @returns {string}
	 */
	getStorageKey() {
		return `AblBackoffice.${this.props.currentCallForApplication.__identity}.callForApplicationItems`
	}

	/**
	 * @param {object} values
	 * @param {function} dispatch
	 * @returns {Promise|boolean}
	 */
	save(values, dispatch) {

		const {t} = this.props;

        this.setState({
            submitting: true
        })

        // We must operate a deep copy to break the binding.
		values = JSON.parse(JSON.stringify(values))

		// We edit the application
		if (this.isEditMode()) {
			this.props.update(values, dispatch)
				.then(application => {
					if (typeof application === 'object') {

						// Flash message for the User.
						flash(
							t('message.application.success.text'),
							t('message.application.success.title')
						)
						window.location = AblCh.successUri
					} else {
                        this.setState({
                            submitting: false
                        })

                        // Error handling...
						flash(
							t('message.edit.error.text', {value: ''}),
							t('message.edit.error.title'),
							'error' // type error
						)
					}

				})
		} else {  // create new!

			// Sanitize item and consider only the valid entries.
			const applicationItems = []
			this.state.callForApplicationItems.map((callForApplicationItem, index) => {
				applicationItems.push({
					preferencePosition: index + 1,
					callForApplicationItem: callForApplicationItem.__identity
				})
			})

			// Make sure we have at least one application item
			if (applicationItems.length > 0) {
				values.applicationItems = applicationItems

				this.props.create(values, null, dispatch)
					.then(application => {

						// Flash message for the User.
						flash(
							t('message.application.success.text') + ' ' + t('message.application.success.email'),
							t('message.application.success.title')
						)

						// Clear basket
						const key = this.getStorageKey()
						localStorage.setItem(key, '')

						// Redirect to list.
						window.location = AblCh.successUri
					}).catch(() => {

            // todo: in case of an exception on creation the api returns 'false', which is not catched as error an will return a false confirmation

            this.setState({
              submitting: false
            })

					// Error handling...
					flash(
						t('message.edit.error.text', {value: ''}),
						t('message.edit.error.title'),
						'error' // type error
					)
				})
			} else {
                this.setState({
                    submitting: false
                })

				// Error handling...
				flash(
					'Bitte wählen Sie im Wohnungsmarkt mindestens eine Wohnung.',
					'Es wurde keine Wohnung ausgewählt.',
					'error'
				)
				return false // do not submit
			}
		}

	}

	/**
	 * @param fields
	 * @returns {*}
	 */
	renderAdditionalApplicants({fields}) {
		const {t} = this.props

		return (
			<div>
				{
					fields.map((fieldPath, index) => {

						return (
							<div key={index}>
								<div className={styles.application__additionalApplicant__header}>
									<button className="btn btn-secondary btn-sm float-right"
									        type="button"
									        onClick={() => fields.remove(index)}>Person entfernen
										<i className="abl-icon-cancel"/>
									</button>
								</div>
								<PartialApplicant fieldPath={fieldPath} isMainApplicant={false}/>
							</div>
						)
					})
				}
				<button className={`btn btn-sm btn-secondary btn-add-person`}
				        type="button"
				        onClick={() => fields.push({})}>
					<i className="abl-icon-plus"/>
					&nbsp;
					{t('action.addPerson')}
				</button>
			</div>
		)
	}

	/**
	 * @returns {int}
	 */
	getNumberOfPeople() {

		const values = this.props.applicationForm.values;
		const mainApplicant = 1;
		const additionalApplicants = values.additionalApplicants
			? values.additionalApplicants.length
			: 0;

		const childrenBirthYears = values.childrenBirthYears
			? values.childrenBirthYears.length
			: 0;
		return mainApplicant + additionalApplicants + childrenBirthYears
	}

	/**
	 * @returns {JSX.Element}
	 */
	renderForm() {
		const {handleSubmit, pristine, reset, t} = this.props;
    const buttonSubmitLabel =  this.isEditMode() ? t('action.update') : t('action.send');

    const linkTagRegulations =
      (
        <a href="/wohnen/wohnungsmarkt" target="_blank">
            Richtlinien zum Vermietungsreglement
        </a>
      )
    const labelTermsRead =
      (
		  <span>
			  Ich habe die {linkTagRegulations} gelesen.
		  </span>
      )
    const labelPrivacyStatementRead =
      (
		  <span>
			  Die <a href="/datenschutz" target="_blank" rel="noopener">Datenschutzerklärung</a> der abl habe ich zur Kenntnis genommen.
		  </span>
      )

    return (
			<form id="application__form" onSubmit={handleSubmit(this.save)}>

				<div className="row">
					<div className="col-12 col-lg-4 order-lg-2">
						{this.isEditMode()
							? (
								<BasketSummaryEdit items={AblCh.application.applicationItems}/>
							) : (
								<BasketSummaryNew items={this.state.callForApplicationItems}
								                  movePositionInBasket={this.movePositionInBasket}
								                  removeFromBasket={this.removeFromBasket}/>
							)
						}
					</div>
					<div className="col-12 col-lg-8 order-lg-1">

						<h3>{t('application.mainApplicant')}</h3>
						<PartialApplicant
							isMainApplicant={true}
							fieldPath="mainApplicant"/>

						<fieldset>
						  <h3>{t('application.householdInformation')}</h3>

						  {this.props.applicationForm && this.props.applicationForm.values && (
							<div className="form-group">
							  <label>
								{t('application.numberOfPeople')}
							  </label>
							  <div className="number-of-persons">
								{this.getNumberOfPeople()}
							  </div>
							</div>
						  )}

						  <div className="additionalApplicants">
							<h3>{t('application.additionalApplicants')}</h3>
							<FieldArray name="additionalApplicants" component={this.renderAdditionalApplicants}/>
						  </div>

						</fieldset>

						<fieldset>
							<h3>{t('application.children')}</h3>
							<FieldArray name="childrenBirthYears"
												t={t}
												component={childrenBirthYearsArray}/>
						</fieldset>

						<fieldset>
							<h3>Diverse Angaben</h3>
							<Field name="pets"
								 component={textField}
								 label={t('application.pets')}/>

							<Field name="liabilityInsurance" component={booleanRadio} required={true}
								 labelTitle={t('application.liabilityInsurance')}/>

							<Field name="rentExceedsOneFourthOfIncome" component={booleanRadio} required={true}
								 labelTitle={t('application.rentExceedsOneFourthOfIncome')}/>
						</fieldset>

						<fieldset>
							<h3>{t('application.currentLeaseInfo')}</h3>

							<Field name="currentLandlord" component={textField}
								   required={true}
								   label={t('application.currentLandlord')}/>

							<Field name="currentLeaseWasCancelled" component={booleanRadio}
								   required={true}
								   labelTitle={t('application.currentLeaseWasCancelled')}/>

							<Field name="cancellationReason" component={textField}
								   label={t('application.cancellationReason')}/>
						</fieldset>

						{this.isEditMode() ? ( null ) : (

							<fieldset>
								<div className="small">
								<h3>Bestimmungen und Vergaberichtlinien</h3>
								<p>Für die Wohnungsvergabe gelten das Vermietungsreglement sowie die Richtlinien zum Vermietungsreglement. Sie finden diese Dokumente auf der Seite <a href="/wohnen/wohnungsmarkt" target="_blank">Wohnungsmarkt</a>.</p>
								<p>In der ersten Bewerbungsrunden werden für die 4.5-Zimmer-Wohnungen und 5.5-Zimmer-Wohnungen lediglich Bewerbungen berücksichtigt, welche die Mindestbelegung gemäss Richtlinien zum Vermietungsreglement Art. 3.2 (4.5-Zimmer-Wohnungen = 3 Personen und 5.5-Zimmer-Wohnungen = 4 Personen) erfüllen.</p>
								<p>Nach der erfolgten Wohnungszuteilung werden die Bewerber/-innen innerhalb von einer Woche nach Anmeldeschluss benachrichtigt. Ebenfalls wird den nichtberücksichtigten Bewerbern/-innen mitgeteilt, auf welchem Platz sie auf der Rangliste sind. Sollte keine Zuteilung der Wunschwohnung möglich sein, wird die abl den Bewerbern/-innen anhand einer Warteliste ein Angebot von noch freien Wohnungen unterbreiten.</p>
								<p>Wenn auf ein Wohnungsangebot der abl innert Wochenfrist keine definitive Zusage seitens des/der Bewerber/-in erfolgt, verfällt das Angebot und es wird der/die nächste Bewerber/-in auf der Rangliste berücksichtigt</p>
								</div>

								<Field name="termsRead" component={checkbox}
									 value={null}
									 addClass="font-size-regular"
									 required={true}
									 label={labelTermsRead}/>

								<Field name="termsAccepted" component={checkbox}
									   value={null}
									   required={true}
									   label={t('application.termsAccepted')}/>

								<Field name="privacyStatementRead" component={checkbox}
										className="checkbox-privacy"
										value={null}
										required={true}
										label={labelPrivacyStatementRead}/>
							</fieldset>
							)
						}

							<Field name="comment" component={textArea}
								   label={t('application.comment')}/>

							<button type="submit"
									className="btn btn-primary"
									disabled={this.state.submitting}
									onClick={handleSubmit(this.save)}>{buttonSubmitLabel}
              </button>

					</div>
				</div>
			</form>
		)
	}

	/**
	 * @returns {JSX.Element}
	 */
	render() {
		const {t} = this.props;

		// Language file is not yet retrieved. Just return null and recheck later.
		if (t('application.comment') !== 'Bemerkung') {
			return null
		} else if (this.isEditMode()) {
			return this.renderForm()
		} else if (this.state.isLoaded === true || this.props.currentCallForApplication !== null) {
			return this.renderForm()
		} else {
			return null
		}

	}
}
